import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { emptyBrandDetails } from "../data/brandMock";
import {
  fetchBrandProfile,
  fetchPaymentConfiguration,
} from "../services/brandService";
import type { BrandDetails } from "../types/brand.types";

/**
 * `failed` only where nothing at all arrived. One half failing still leaves a
 * form worth showing, and says so in a toast instead — the two records are
 * separate endpoints, and an empty screen would hide the half that answered.
 */
export type BrandLoadStatus = "loading" | "ready" | "failed";

export interface BrandDetailsState {
  /**
   * The saved brand, once it has arrived — `null` until then. A new identity
   * per load, which is what the page seeds its form state from.
   */
  loaded: BrandDetails | null;
  status: BrandLoadStatus;
  /** What the failed state's retry calls. */
  reload: () => void;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

export const BRAND_DETAILS_QUERY_KEY = ["brand", "details"] as const;

/**
 * The dietitian's saved brand, both records, fetched together on open.
 *
 * `allSettled` rather than `all`: they are independent records, and a payment
 * endpoint that is down is no reason to leave the brand fields empty. Whatever
 * did arrive fills the form; whatever did not falls back to what is already
 * cached for it (so a background revalidation losing one half does not wipe a
 * field that loaded fine a moment ago) and is named in a toast either way.
 *
 * A record that has never been saved is not a failure — `fetchBrandProfile`
 * and `fetchPaymentConfiguration` answer `null` for one, and the form simply
 * opens empty, which is the right screen for a first visit.
 *
 * Backed by TanStack Query: a screen visited again shows the last-read brand
 * immediately while a fresh read runs behind it, rather than making the
 * Brand screen wait on the network every time it opens.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useBrandDetails(): BrandDetailsState {
  const getToken = useTokenGetter();
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: BRAND_DETAILS_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const [profileResult, paymentResult] = await Promise.allSettled([
        fetchBrandProfile(getToken, signal),
        fetchPaymentConfiguration(getToken, signal),
      ]);

      if (profileResult.status === "rejected") {
        console.error("brand profile failed to load:", profileResult.reason);
      }
      if (paymentResult.status === "rejected") {
        console.error(
          "payment configuration failed to load:",
          paymentResult.reason
        );
      }

      // Nothing came back at all: a real failure, and there is nothing
      // partial worth keeping over what was cached before.
      if (
        profileResult.status === "rejected" &&
        paymentResult.status === "rejected"
      ) {
        throw profileResult.reason;
      }

      const previous =
        queryClient.getQueryData<BrandDetails>(BRAND_DETAILS_QUERY_KEY);

      const merged: BrandDetails = {
        ...emptyBrandDetails,
        ...(previous ?? {}),
        ...(profileResult.status === "fulfilled"
          ? profileResult.value ?? {}
          : {}),
        ...(paymentResult.status === "fulfilled"
          ? paymentResult.value ?? {}
          : {}),
      };

      // Half a form is worth showing, but not without saying which half is
      // missing — those fields are blank (or held at their last good value)
      // because the read did not arrive, not because nothing was ever saved
      // in them.
      if (profileResult.status === "rejected") {
        toast.error(
          "Could not load your brand details",
          failureMessage(profileResult.reason)
        );
      }
      if (paymentResult.status === "rejected") {
        toast.error(
          "Could not load your payment details",
          failureMessage(paymentResult.reason)
        );
      }

      return merged;
    },
  });

  return {
    loaded: query.data ?? null,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    reload: () => {
      void query.refetch();
    },
  };
}
